/**
 * Rich `prefillAnswers` fixtures aligned with estate-client-schemas `data-model.json`
 * `prefillSource` sample (read-only suggestions at session start).
 *
 * `testator.profile.name`/`testator.profile.address`/`testator.spouse` are the
 * normalized shape — same dot-paths as `answerResponse.testator.*` — per the
 * "prefillSource normalization" changelog entry. There is no separate flat
 * `person`/`primaryAddress`/`spouse`/`suggestedSpouse` root anymore; real
 * templates only ever read `prefillSource.testator.*`. `profile.name.fullName`
 * is client-derived; address displays are composed from the normalized address
 * fields at render time.
 */
import type {EstateAnswersBySection} from './types';

const SHARED_PEOPLE = [
  {id: 'person_1', fullName: 'John Smith'},
  {id: 'person_2', fullName: 'Jane Smith'},
  {id: 'person_3', fullName: 'Michael Smith'},
  {id: 'person_4', fullName: 'Sarah Jones'},
];

const SHARED_CHILDREN = {
  hasChildren: 'true',
  childrenList: [
    {id: 'child_a1b2c3', fullName: 'Emma Smith', dateOfBirth: '2015-03-14'},
    {id: 'child_d4e5f6', fullName: 'Liam Smith', dateOfBirth: '2018-07-22'},
  ],
};

const SHARED_TESTATOR_PREFILL = {
  profile: {
    name: {firstName: 'John', middleName: '', lastName: 'Smith', fullName: 'John Smith'},
    address: {
      country: 'United States',
      address: '123 Main St',
      address2: '',
      city: 'Los Angeles',
      state: 'CA',
      zip: '90210',
    },
  },
  primaryAddressConfirmed: 'true',
  relationshipStatus: 'married',
  spouseConfirmed: 'true',
  spouse: {
    firstName: 'Jane',
    middleName: 'Marie',
    lastName: 'Smith',
    fullName: 'Jane Smith',
    dateOfBirth: '03/15/1988',
    email: 'jane.smith@email.com',
  },
};

function sharedPrefillBase(): EstateAnswersBySection {
  return {
    people: SHARED_PEOPLE,
    children: SHARED_CHILDREN,
    // guardians: {
    //   nominateGuardians: 'true',
    //   guardians: [{personId: 'person_3', role: 'guardian'}],
    // },
    // beneficiaries: {
    //   primaryBeneficiaries: [],
    //   hasBackupBeneficiary: false,
    //   backupBeneficiaries: [],
    //   minimumAgeDistribution: '18',
    // },
    // pourOverWill: {
    //   executors: [{personId: 'person_3', role: 'executor'}],
    // },
    // financialPOA: {
    //   poa: [
    //     {personId: 'person_3', role: 'primary_poa'},
    //     {personId: 'person_4', role: 'successor_poa'},
    //   ],
    // },
    // health: {
    //   poa: [
    //     {personId: 'person_3', role: 'primary_poa'},
    //     {personId: 'person_4', role: 'successor_poa'},
    //   ],
    //   lifeSustainingTreatment: 'no_prolonging',
    //   artificialNutritionPreference: 'no_if_imminent',
    //   organDonationPreference: 'donate_all',
    //   painMedication: true,
    //   authorizedPersons: [{personId: 'person_3', relationship: 'Sibling'}],
    //   successorTrusteeMedicalAccess: true,
    //   medicalRecordsExpiration: 'no_expiration',
    // },
    testator: SHARED_TESTATOR_PREFILL,
  };
}

/** Will-package prefill suggestions. */
export function buildWillPrefillAnswers(): EstateAnswersBySection {
  return sharedPrefillBase();
}

/** Trust-package prefill suggestions — includes trust + spouse reciprocal stubs. */
export function buildTrustPrefillAnswers(): EstateAnswersBySection {
  return {
    ...sharedPrefillBase(),
    trust: {
      name: 'The Smith Family Trust',
      type: 'joint',
      primaryTrustees: [{personId: 'person_1'}, {personId: 'person_2'}],
      trustees: [{personId: 'person_3', role: 'successor_trustee'}],
    },
    spousePourOverWill: {
      executors: [{personId: 'person_2', role: 'executor'}],
    },
    spouseFinancialPOA: {
      poa: [{personId: 'person_2', role: 'primary_poa'}],
    },
    spouseHealth: {
      poa: [{personId: 'person_2', role: 'primary_poa'}],
      lifeSustainingTreatment: 'no_prolonging',
      artificialNutritionPreference: 'no_if_imminent',
      organDonationPreference: 'donate_all',
      painMedication: true,
      authorizedPersons: [{personId: 'person_2', relationship: 'Spouse'}],
      medicalRecordsExpiration: 'no_expiration',
    },
  };
}
